import { useCallback, useEffect, useState } from "react";
import {
  fetchDashboardAiSummaryToggle,
  updateDashboardAiSummaryToggle,
  type DashboardAiSummaryToggleResponse,
  type DashboardAiSummaryUsage,
  type DashboardNarrativeKey,
} from "src/services/DashboardNarrativeAPI";

export interface AiSummaryToggleState {
  enabled: boolean;
  featureAvailable: boolean;
  usage?: DashboardAiSummaryUsage;
  loading: boolean;
  saving: boolean;
  ready: boolean;
}

export interface AiSummaryToggleControl extends AiSummaryToggleState {
  setEnabled: (_enabled: boolean) => void;
}

const DEFAULT_STATE: AiSummaryToggleState = {
  enabled: true,
  featureAvailable: true,
  loading: true,
  saving: false,
  ready: false,
};

// One state entry per dashboard group so every mounted report in that group
// (e.g. Overview/Sales/Inventory/Customers) reads and writes the same toggle.
const groupState = new Map<DashboardNarrativeKey, AiSummaryToggleState>();
const groupListeners = new Map<DashboardNarrativeKey, Set<() => void>>();
const groupRequestInFlight = new Map<
  DashboardNarrativeKey,
  Promise<DashboardAiSummaryToggleResponse>
>();

const getState = (dashboardKey: DashboardNarrativeKey): AiSummaryToggleState =>
  groupState.get(dashboardKey) ?? DEFAULT_STATE;

const setState = (
  dashboardKey: DashboardNarrativeKey,
  patch: Partial<AiSummaryToggleState>,
) => {
  groupState.set(dashboardKey, { ...getState(dashboardKey), ...patch });
  groupListeners.get(dashboardKey)?.forEach(listener => listener());
};

const applyResponse = (
  dashboardKey: DashboardNarrativeKey,
  response: DashboardAiSummaryToggleResponse,
) => {
  setState(dashboardKey, {
    enabled: Boolean(response.enabled),
    featureAvailable: response.feature_available !== false,
    usage: response.usage,
    loading: false,
    saving: false,
    ready: true,
  });
};

const subscribe = (dashboardKey: DashboardNarrativeKey, listener: () => void) => {
  const listeners = groupListeners.get(dashboardKey) ?? new Set<() => void>();
  listeners.add(listener);
  groupListeners.set(dashboardKey, listeners);

  return () => {
    listeners.delete(listener);
  };
};

const loadToggle = (dashboardKey: DashboardNarrativeKey) => {
  if (getState(dashboardKey).ready || groupRequestInFlight.has(dashboardKey)) {
    return;
  }

  const request = fetchDashboardAiSummaryToggle(dashboardKey).finally(() => {
    groupRequestInFlight.delete(dashboardKey);
  });
  groupRequestInFlight.set(dashboardKey, request);

  request
    .then(response => applyResponse(dashboardKey, response))
    .catch(() => {
      // Keep the panel usable if the toggle cannot be read; the backend still
      // enforces the real state on /narrative.
      setState(dashboardKey, { loading: false, ready: true });
    });
};

/**
 * Reads and writes the per-user AI Summary toggle for a dashboard group. The
 * server value is the only source of truth (nothing is cached locally), so the
 * state survives refresh and login, and flipping it from one report in the
 * group updates every other mounted report in that group immediately.
 */
export function useAiSummaryToggle(
  dashboardKey: DashboardNarrativeKey,
): AiSummaryToggleControl {
  const [state, setLocalState] = useState<AiSummaryToggleState>(() => getState(dashboardKey));

  useEffect(() => {
    const sync = () => setLocalState(getState(dashboardKey));
    const unsubscribe = subscribe(dashboardKey, sync);
    sync();
    loadToggle(dashboardKey);

    return unsubscribe;
  }, [dashboardKey]);

  const setEnabled = useCallback(
    (enabled: boolean) => {
      const current = getState(dashboardKey);
      if (current.saving || current.enabled === enabled) {
        return;
      }

      setState(dashboardKey, { enabled, saving: true });

      updateDashboardAiSummaryToggle(dashboardKey, enabled)
        .then(response => applyResponse(dashboardKey, response))
        .catch(() => {
          setState(dashboardKey, { enabled: current.enabled, saving: false });
        });
    },
    [dashboardKey],
  );

  return { ...state, setEnabled };
}
